import ApplicationController from './application_controller';
import CableReady from 'cable_ready';
import consumer from '../channels/consumer';

// Characters arriving faster than this threshold (ms) are treated as scanner input.
// Typical scanner inter-character delay: 1–3 ms. Fast human typing: ~80 ms+.
const SCANNER_THRESHOLD_MS = 50;

// Maps event.code (physical key, layout-independent) to the character produced
// on a standard US QWERTY keyboard, with and without Shift.
// QR codes use printable ASCII only, so this covers all needed characters.
const CODE_TO_CHAR = {
  Backquote:    ['`', '~'],  Minus:        ['-', '_'],  Equal:        ['=', '+'],
  BracketLeft:  ['[', '{'],  BracketRight: [']', '}'],  Backslash:    ['\\', '|'],
  Semicolon:    [';', ':'],  Quote:        ["'", '"'],  Comma:        [',', '<'],
  Period:       ['.', '>'],  Slash:        ['/', '?'],
  Space:        [' ', ' '],
  Digit0: ['0', ')'], Digit1: ['1', '!'], Digit2: ['2', '@'], Digit3: ['3', '#'],
  Digit4: ['4', '$'], Digit5: ['5', '%'], Digit6: ['6', '^'], Digit7: ['7', '&'],
  Digit8: ['8', '*'], Digit9: ['9', '('],
  KeyA: ['a', 'A'], KeyB: ['b', 'B'], KeyC: ['c', 'C'], KeyD: ['d', 'D'],
  KeyE: ['e', 'E'], KeyF: ['f', 'F'], KeyG: ['g', 'G'], KeyH: ['h', 'H'],
  KeyI: ['i', 'I'], KeyJ: ['j', 'J'], KeyK: ['k', 'K'], KeyL: ['l', 'L'],
  KeyM: ['m', 'M'], KeyN: ['n', 'N'], KeyO: ['o', 'O'], KeyP: ['p', 'P'],
  KeyQ: ['q', 'Q'], KeyR: ['r', 'R'], KeyS: ['s', 'S'], KeyT: ['t', 'T'],
  KeyU: ['u', 'U'], KeyV: ['v', 'V'], KeyW: ['w', 'W'], KeyX: ['x', 'X'],
  KeyY: ['y', 'Y'], KeyZ: ['z', 'Z'],
  Numpad0: ['0', '0'], Numpad1: ['1', '1'], Numpad2: ['2', '2'], Numpad3: ['3', '3'],
  Numpad4: ['4', '4'], Numpad5: ['5', '5'], Numpad6: ['6', '6'], Numpad7: ['7', '7'],
  Numpad8: ['8', '8'], Numpad9: ['9', '9'],
  NumpadDecimal: ['.', '.'], NumpadDivide: ['/', '/'], NumpadMultiply: ['*', '*'],
  NumpadSubtract: ['-', '-'], NumpadAdd: ['+', '+'],
};

// Resolve the US-QWERTY character for a keydown event, ignoring the OS layout.
function codeToUsChar(event) {
  const entry = CODE_TO_CHAR[event.code];
  if (!entry) return null;
  return event.shiftKey ? entry[1] : entry[0];
}

export default class extends ApplicationController {

  static targets = ['instruction', 'log', 'qrInput', 'makeSensorsButton'];

  initialize() {
    super.initialize();

    // QR scanner detection state
    this._scanBuffer = '';
    this._lastKeyTime = 0;
    this._scannerMode = false; // true once we detect scanner-speed input

    const deliveryId = this.element.dataset.deliveryId;
    const locale = document.querySelector('body').dataset.locale;
    const mainController = this;

    consumer.subscriptions.create(
      { channel: 'AssignedSensorsChannel', delivery_id: deliveryId, locale: locale },
      {
        received(data) {
          if (data.cableReady) {
            CableReady.perform(data.operations);
          }
          if (data.instruction) {
            mainController.setInstruction(data.instruction);
          }
          if (data.log) {
            mainController.appendLogEntry(data.log, data.log_type);
          }
          if (data.redirect_to && data.redirect_after_ms) {
            setTimeout(() => { document.location = data.redirect_to; }, data.redirect_after_ms);
          } else if (data.redirect_to) {
            document.location = data.redirect_to;
          }
        }
      }
    );
  }

  connect() {
    super.connect();
    // Listen on the whole document so the user doesn't have to click the input first.
    this._onKeydown = this._handleGlobalKeydown.bind(this);
    document.addEventListener('keydown', this._onKeydown);
  }

  disconnect() {
    document.removeEventListener('keydown', this._onKeydown);
    super.disconnect();
  }

  // ── Global keydown handler ────────────────────────────────────────────────

  _handleGlobalKeydown(event) {
    const now = Date.now();
    const gap = now - this._lastKeyTime;
    this._lastKeyTime = now;

    const isEnter = event.code === 'Enter' || event.code === 'NumpadEnter';

    // Resolve character from physical key position (US QWERTY), ignoring OS layout.
    const char = isEnter ? null : codeToUsChar(event);

    // Ignore keys we can't map (function keys, arrows, modifiers, etc.)
    if (!char && !isEnter) return;
    // Ignore combinations with Ctrl/Alt/Meta (browser shortcuts)
    if (event.ctrlKey || event.altKey || event.metaKey) return;

    if (isEnter) {
      if (this._scanBuffer.length > 0) {
        if (this._scannerMode) {
          this._submitScan(this._scanBuffer);
        } else {
          this.appendLogEntry(
            `Warning: input "${this._scanBuffer}" submitted without scanner detection – inter-key delay was too slow. Check that a QR scanner is being used.`,
            'error'
          );
          this._submitScan(this._scanBuffer);
        }
      }
      this._resetBuffer();
      return;
    }

    // Detect scanner vs. human based on inter-character gap.
    if (this._scanBuffer.length === 0) {
      this._scannerMode = true; // optimistic – cleared if a gap is too large
    } else if (gap > SCANNER_THRESHOLD_MS) {
      this._scannerMode = false;
    }

    this._scanBuffer += char;

    // Mirror the buffer into the visible (disabled) input field.
    if (this.hasQrInputTarget) {
      this.qrInputTarget.value = this._scanBuffer;
    }

    // Always prevent the key from reaching other inputs.
    event.preventDefault();

    // Auto-submit after SCANNER_THRESHOLD_MS of silence – handles scanners without Enter suffix.
    clearTimeout(this._scanTimeout);
    if (this._scannerMode) {
      this._scanTimeout = setTimeout(() => {
        if (this._scanBuffer.length > 0) {
          this._submitScan(this._scanBuffer);
          this._resetBuffer();
        }
      }, SCANNER_THRESHOLD_MS);
    }
  }

  _submitScan(address) {
    const trimmed = address.trim();
    if (trimmed.length < 3) return;
    this.stimulate('AssignedSensors#assign_sensor', this.element, trimmed);
  }

  useGeneratedAddress(event) {
    const address = event.currentTarget.dataset.address;
    if (this.hasQrInputTarget) {
      this.qrInputTarget.value = address;
    }
    this._submitScan(address);
  }

  _resetBuffer() {
    clearTimeout(this._scanTimeout);
    this._scanBuffer = '';
    this._scannerMode = false;
    // Keep qrInputTarget value visible until next scan starts
  }

  // ── UI helpers ────────────────────────────────────────────────────────────

  setInstruction(text) {
    if (this.hasInstructionTarget) {
      this.instructionTarget.textContent = text;
    }
  }

  appendLogEntry(message, type = 'success') {
    if (!this.hasLogTarget) return;

    const entry = document.createElement('div');
    entry.classList.add('assigned-sensors-log-entry', `assigned-sensors-log-entry--${type}`);
    const time = new Date().toLocaleTimeString(navigator.language, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    entry.textContent = `${time} – ${message}`;

    // Prepend – newest on top
    this.logTarget.insertBefore(entry, this.logTarget.firstChild);
  }

  // Override ApplicationController appendLog to avoid default behaviour
  appendLog(_message) {}
}
